import React, { useState } from "react";
import styled from "styled-components";
import { PROJECT_STATUS_LABEL, ProjectStatus } from "@lifeat/shared/projects/ProjectStatus";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { DropdownItem } from "../../task-management/components/TaskTagDropdownComponents";
import { UpdateProjectStatusEvent } from "../AmplitudeProjectsEvents";
import { ProjectsService } from "../ProjectsService";

const ProjectStatusWrapper = styled(DropdownMenuComponents.Trigger)`
    border-radius: ${Styles.borderRadiusSharp};
    border: 1px solid ${(props) => props.theme.borderPrimary};
    padding: 4px 8px;
`;

const ProjectStatusTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    width: fit-content;
    max-width: 150px;
    height: fit-content;

    padding: 2px;
    font-size: ${FontSize.normal};
    border-radius: 2px;

    cursor: pointer;
    overflow: hidden;
    color: ${(props) => props.theme.textColorPrimary};
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const RadioButtonItemContainer = styled.div`
    display: flex;
    gap: 7px;
`;

const DEBOUNCE_DELAY_MS = 1000;

interface ProjectStatusPickerProps {
    projectId: string;
    status: ProjectStatus;
}
export default function ProjectStatusPicker(props: ProjectStatusPickerProps) {
    const [currProjectStatus, setCurrProjectStatus] = useProjectStatusInput(props.projectId, props.status);
    const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);

    return (
        <DropdownMenuComponents.Root
            open={isStatusPopoverOpen}
            onOpenChange={(open: boolean) => setIsStatusPopoverOpen(open)}
        >
            <ProjectStatusWrapper>
                <ProjectStatusTrigger>
                    {PROJECT_STATUS_LABEL[currProjectStatus]}
                    <CaretIconToggle showOpen={isStatusPopoverOpen} />
                </ProjectStatusTrigger>
            </ProjectStatusWrapper>

            <DropdownMenuComponents.Content>
                <DropdownMenuComponents.HeaderItem label={"Project Status"} />

                {Object.values(ProjectStatus).map((status) => (
                    <DropdownItem key={`project-status-${status}`} onClick={() => setCurrProjectStatus(status)}>
                        <RadioButtonItemContainer>
                            <input
                                id={`project-status-picker-option-${status}`}
                                type="radio"
                                value={status}
                                style={{ display: "none" }}
                                checked={status === currProjectStatus}
                            />
                            <label htmlFor={`project-status-picker-option-${status}`}>
                                <ProjectStatusTrigger>{PROJECT_STATUS_LABEL[status]}</ProjectStatusTrigger>
                            </label>
                        </RadioButtonItemContainer>
                    </DropdownItem>
                ))}
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}

function useProjectStatusInput(
    projectId: string,
    initialStatus: ProjectStatus,
): [currProjectStatus: ProjectStatus, setCurrProjectStatus: (status: ProjectStatus) => void] {
    const amplitudeService = AmplitudeService.getInstance();

    const [currProjectStatus, setCurrProjectStatus] = useDebouncedInput(
        initialStatus ?? "OPEN",
        async (updatedStatus: ProjectStatus) => {
            if (updatedStatus !== initialStatus) {
                amplitudeService.logEvent(new UpdateProjectStatusEvent(initialStatus, updatedStatus));
                await ProjectsService.updateProjectStatus(projectId, updatedStatus);
            }
        },
        DEBOUNCE_DELAY_MS,
    );

    return [currProjectStatus, setCurrProjectStatus];
}
